import React, { useState, useContext } from "react";
import { useTranslation } from 'react-i18next';
import { AuthContext } from './components/Provider';
import { useToast } from './components/hooks/ToastContext';
import './components/Templates.js';
import { CustomButton6, CustomInputBox1, CustomTextArea } from "./components/Templates.js";
import Spinner from './components/common/Spinner';

const ContactForm = () => {
  const { t, i18n } = useTranslation('common');
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();
  
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.profile?.full_name || '',
    email: user?.profile?.email || '',
    message: ''
  });

  const handleInputChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.message) {
      addToast({
        type: 'error',
        title: t('forms.validation.error'),
        description: t('forms.validation.all_fields_required')
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      addToast({
        type: 'error',
        title: t('forms.validation.error'),
        description: t('forms.validation.invalid_email')
      });
      return;
    }

    setLoading(true);

    try {
      // Send to Cloudflare Worker
      const response = await fetch('https://contact-support-email-worker.davidskuzmans.workers.dev', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'contact',
          name: formData.name,
          email: formData.email,
          message: formData.message,
          language: i18n.language || 'lv'
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
        addToast({
          type: 'success',
          title: t('contact.success.title'),
          description: t('contact.success.message')
        });
        
        // Reset form after 5 seconds
        setTimeout(() => {
          setSubmitted(false);
          setFormData({
            name: user?.profile?.full_name || '',
            email: user?.profile?.email || '',
            message: ''
          });
        }, 5000);
      } else {
        // Handle rate limiting
        if (response.status === 429) {
          addToast({
            type: 'error',
            title: t('contact.error.rate_limit_title'),
            description: t('contact.error.rate_limit_message')
          });
        } else {
          addToast({
            type: 'error',
            title: t('contact.error.title'),
            description: data.error || t('contact.error.message')
          });
        }
      }
    } catch (error) {
      console.error('Contact form error:', error);
      addToast({
        type: 'error',
        title: t('contact.error.title'),
        description: t('contact.error.network')
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '300px' 
      }}>
        <Spinner size="medium" showText={true} />
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '40px',
        background: 'var(--transparent-color)',
        borderRadius: '15px'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
        <h2 style={{ color: 'var(--accent-color)', marginBottom: '15px' }}>
          {t('contact.success.thank_you')}
        </h2>
        <p style={{ color: 'var(--main-color)' }}>
          {t('contact.success.will_respond')}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="CFm-BackG">
        <CustomInputBox1
          type="text"
          placeholder={t('contact.form.name')}
          value={formData.name}
          onChange={handleInputChange('name')}
          required
        />
      </div>
      
      <div className="CFm-BackG">
        <CustomInputBox1
          type="email"
          placeholder={t('contact.form.email')}
          value={formData.email}
          onChange={handleInputChange('email')}
          required
        />
      </div>
      
      <div className="CFm-BackG">
        <CustomTextArea
          placeholder={t('contact.form.message')}
          value={formData.message}
          onChange={handleInputChange('message')}
          rows={5}
          maxLength={1000}
        />
      </div>
      
      <div style={{ textAlign: 'center' }}>
        <CustomButton6
          type="submit"
          label={t('contact.form.send')}
        />
      </div>
    </form>
  );
};

export default ContactForm;
